import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'

// What "Make conditional" wraps the selection in: part of one line becomes
// an inline span; whole blocks (or a selection across blocks) a section.
export function conditionalWrapKind(state: EditorState): 'span' | 'section' {
  const { $from, $to, empty } = state.selection
  if (
    !empty &&
    $from.parent.isTextblock &&
    $from.sameParent($to) &&
    ($from.parentOffset > 0 || $to.parentOffset < $from.parent.content.size)
  ) {
    return 'span'
  }
  return 'section'
}

// Whether the selection could be wrapped in a span at all: inside one line.
export function canWrapInSpan(state: EditorState): boolean {
  const { $from, $to, empty } = state.selection
  return !empty && $from.parent.isTextblock && $from.sameParent($to)
}

// The item "Make conditional" configures rather than wraps: one inline node
// that takes its own condition (an observation, an instrument, a span),
// selected alone within one line, so it never gains a second condition.
// Space beside it does not count; a span it sits in is looked into.
export function ownConditionTarget(
  doc: PMNode,
  from: number,
  to: number
): number | null {
  if (from === to) {
    return null
  }
  const line = textblockStart(doc.resolve(from))
  if (line === null || line !== textblockStart(doc.resolve(to))) {
    return null
  }
  let found: number | null = null
  let other = false
  doc.nodesBetween(from, to, (node, pos) => {
    if (other) {
      return false
    }
    if (node.isText) {
      const selected = (node.text ?? '').slice(
        Math.max(0, from - pos),
        to - pos
      )
      other = selected.trim() !== ''
      return false
    }
    if (!node.isInline) {
      return true
    }
    const whole = pos >= from && pos + node.nodeSize <= to
    if (!whole && node.inlineContent) {
      return true
    }
    if (whole && found === null && 'showWhen' in node.attrs) {
      found = pos
    } else {
      other = true
    }
    return false
  })
  return other ? null : found
}

function textblockStart($pos: ResolvedPos): number | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).isTextblock) {
      return $pos.before(depth)
    }
  }
  return null
}
